import {
  legacyFetch as legacyFetchImpl,
  camelizedJSONFetch as camelizedJSONFetchImpl,
  fetchWithInterceptors,
  createCamelizedJSONHandler,
  attachBrowserHeaders,
  handleLegacyError,
  type CamelizeJSONOptions,
} from '@squareup/util-api-client';
import { DashboardQueryContext } from '../context';

/**
 * Convenience method to apply global DashboardQuery settings to fetch requests.
 */
export const legacyFetch = async<T>(...args: Parameters<typeof fetch>) => {
  const [resource, options] = args;
  const { baseUrl } = DashboardQueryContext;
  if (baseUrl && typeof resource === 'string' && !resource.startsWith('http')) {
    // Prepend baseUrl to the resource if it is a relative URL
    const url = new URL(resource, baseUrl);
    return legacyFetchImpl<T>(url.toString(), options);
  }

  // If resource is already an absolute URL or baseUrl is not set, use it as is
  return legacyFetchImpl<T>(resource, options);
};

export const camelizedJSONFetch = async<T>(...args: Parameters<typeof fetch>) => {
  const [resource, options] = args;
  const { baseUrl } = DashboardQueryContext;
  if (baseUrl && typeof resource === 'string' && !resource.startsWith('http')) {
    // Prepend baseUrl to the resource if it is a relative URL
    const url = new URL(resource, baseUrl);
    return camelizedJSONFetchImpl<T>(url.toString(), options);
  }

  // If resource is already an absolute URL or baseUrl is not set, use it as is
  return camelizedJSONFetchImpl<T>(resource, options);
};

/**
 * Like camelizedJSONFetch but with custom camelization options.
 * Useful for endpoints where deep camelization would mangle map keys.
 */
export const createCustomCamelizedJSONFetch = (camelizeOptions: CamelizeJSONOptions) => {
  const handler = createCamelizedJSONHandler(camelizeOptions);
  return async <T>(...args: Parameters<typeof fetch>) => {
    const [resource, options] = args;
    const { baseUrl } = DashboardQueryContext;
    const resolvedResource = baseUrl && typeof resource === 'string' && !resource.startsWith('http')
      ? new URL(resource, baseUrl).toString()
      : resource;
    return fetchWithInterceptors(resolvedResource, options, [handler<T>, handleLegacyError, attachBrowserHeaders]) as T;
  };
};
